import { useFormatoNoBlur } from '@/hooks/use-formato-no-blur';
import { RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { decimalDigitado, fracaoParaPercentualTexto, formatarCusto, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Campo, CenarioForm } from './tipos';

/** Valores atuais (da API) de cada premissa, so para mostrar "base" e posicionar o slider. Nada e calculado aqui. */
export interface ValoresBase {
  /** Fracoes ("0.2") vindas de resultado do motor da base. */
  lucroDesejado: string;
  despesaVariavel: string;
  despesaFixa: string;
  taxaFinanceira: string;
  /** Dinheiro ("6.23") da composicao da base. */
  custo: string;
}

export interface PremissasProps {
  cenario: CenarioForm;
  base: ValoresBase | null;
  invalidos: Campo[];
  onCampo: (campo: Campo, valor: string) => void;
  onNome: (nome: string) => void;
  onLimpar: () => void;
  desabilitado?: boolean;
}

const ROTULO = 'font-medium';
const RANGE = 'w-full accent-primary';

function Cabecalho({ htmlFor, rotulo, valor, restaurar }: { htmlFor: string; rotulo: string; valor: string; restaurar?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label htmlFor={htmlFor} className={ROTULO}>
        {rotulo}
      </label>
      <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
        <span className="tabular font-semibold">{valor}</span>
        {restaurar && (
          <button type="button" onClick={restaurar} aria-label={`Voltar ${rotulo} ao valor atual`} className="rounded p-0.5 text-ink-muted hover:bg-line-soft">
            <RotateCcw aria-hidden="true" className="size-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}

function CampoInvalido({ id, invalido, children }: { id: string; invalido: boolean; children: React.ReactNode }) {
  return (
    <div>
      {children}
      {invalido && (
        <p id={id} role="alert" className="mt-1 text-caption text-danger-ink">
          Valor inválido.
        </p>
      )}
    </div>
  );
}

/** Slider + campo de percentual. Vazio = valor atual; mexer no slider grava o texto (pt-BR) no campo. */
function PercentualComSlider({
  id, rotulo, valor, baseFracao, min, max, step, sinal, invalido, desabilitado, onChange,
}: {
  id: string; rotulo: string; valor: string; baseFracao: string | null; min: number; max: number; step: number;
  /** Mostra "+" nos positivos (variacao). */
  sinal?: boolean; invalido: boolean; desabilitado?: boolean; onChange: (v: string) => void;
}) {
  const baseTexto = baseFracao === null ? '' : fracaoParaPercentualTexto(baseFracao);
  const efetivo = valor.trim() !== '' ? valor : baseTexto;
  const posicao = efetivo.replace(',', '.').replace(/^\+/, '') || String(min);
  const exibido = valor.trim() !== '' ? `${sinal && !valor.trim().startsWith('-') && !valor.trim().startsWith('+') ? '+' : ''}${valor.trim()}%` : baseFracao === null ? '—' : `${formatarFracaoPercentual(baseFracao)} (base)`;
  return (
    <CampoInvalido id={`${id}-e`} invalido={invalido}>
      <Cabecalho htmlFor={`${id}-n`} rotulo={rotulo} valor={exibido} restaurar={valor.trim() !== '' ? () => onChange('') : undefined} />
      <input
        id={id}
        type="range"
        aria-label={`${rotulo} (controle)`}
        min={min}
        max={max}
        step={step}
        value={posicao}
        disabled={desabilitado || baseFracao === null}
        onChange={(e) => onChange(e.target.value.replace('.', ','))}
        className={RANGE}
      />
      <Input
        id={`${id}-n`}
        inputMode="decimal"
        autoComplete="off"
        placeholder={baseTexto || '—'}
        value={valor}
        disabled={desabilitado}
        aria-invalid={invalido || undefined}
        aria-describedby={invalido ? `${id}-e` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-28 tabular"
      />
    </CampoInvalido>
  );
}

function PercentualSimples({
  id, rotulo, valor, baseFracao, invalido, desabilitado, onChange,
}: { id: string; rotulo: string; valor: string; baseFracao: string | null; invalido: boolean; desabilitado?: boolean; onChange: (v: string) => void }) {
  const baseTexto = baseFracao === null ? '' : fracaoParaPercentualTexto(baseFracao);
  return (
    <CampoInvalido id={`${id}-e`} invalido={invalido}>
      <Cabecalho
        htmlFor={id}
        rotulo={rotulo}
        valor={valor.trim() !== '' ? `${valor.trim()}%` : baseFracao === null ? '—' : `${formatarFracaoPercentual(baseFracao)} (base)`}
        restaurar={valor.trim() !== '' ? () => onChange('') : undefined}
      />
      <Input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        placeholder={baseTexto || '—'}
        value={valor}
        disabled={desabilitado}
        aria-invalid={invalido || undefined}
        aria-describedby={invalido ? `${id}-e` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-28 tabular"
      />
    </CampoInvalido>
  );
}

function Dinheiro({
  id, rotulo, valor, baseDinheiro, nota, invalido, desabilitado, tipo, onChange,
}: { id: string; rotulo: string; valor: string; baseDinheiro: string | null; nota?: string; invalido: boolean; desabilitado?: boolean; tipo: 'dinheiro' | 'custo'; onChange: (v: string) => void }) {
  const formatado = useFormatoNoBlur(valor, tipo);
  const moeda = tipo === 'custo' ? formatarCusto : formatarMoeda;
  return (
    <CampoInvalido id={`${id}-e`} invalido={invalido}>
      <Cabecalho
        htmlFor={id}
        rotulo={rotulo}
        valor={valor.trim() !== '' ? moeda(decimalDigitado(valor)) : baseDinheiro === null ? '—' : `${moeda(baseDinheiro)} (base)`}
        restaurar={valor.trim() !== '' ? () => onChange('') : undefined}
      />
      <Input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        placeholder="0,00"
        {...formatado}
        disabled={desabilitado}
        aria-invalid={invalido || undefined}
        aria-describedby={invalido ? `${id}-e` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-28 tabular"
      />
      {nota && <p className="mt-1 text-caption text-ink-muted">{nota}</p>}
    </CampoInvalido>
  );
}

/** Premissas do cenario em edicao (design Simulador: "Premissas"). Sem calculo: texto digitado vai para a API. */
export function Premissas({ cenario, base, invalidos, onCampo, onNome, onLimpar, desabilitado }: PremissasProps) {
  const c = cenario.campos;
  const inv = (k: Campo) => invalidos.includes(k);
  const set = (k: Campo) => (v: string) => onCampo(k, v);
  return (
    <section aria-label="Premissas" className="flex flex-[1_1_300px] flex-col gap-[18px] rounded-2xl border border-border bg-card px-5 py-[18px]">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-title font-semibold">Premissas</h2>
        <button type="button" onClick={onLimpar} className={cn('text-small text-ink-muted hover:text-ink', desabilitado && 'pointer-events-none opacity-50')}>
          Limpar premissas
        </button>
      </div>
      <div>
        <label htmlFor="p-nome" className="mb-1.5 block text-caption font-medium text-ink-secondary">
          Nome do cenário
        </label>
        <Input id="p-nome" value={cenario.nome} maxLength={60} placeholder={`Cenário ${cenario.id}`} onChange={(e) => onNome(e.target.value)} disabled={desabilitado} className="h-9" />
      </div>
      <PercentualComSlider
        id="p-custo-var" rotulo="Variação do custo" valor={c.custoVariacao} baseFracao="0" min={-20} max={30} step={0.5} sinal
        invalido={inv('custoVariacao')} desabilitado={desabilitado || c.custo.trim() !== ''} onChange={set('custoVariacao')}
      />
      <PercentualComSlider
        id="p-lucro" rotulo="Lucro desejado" valor={c.lucroDesejado} baseFracao={base?.lucroDesejado ?? null} min={0} max={40} step={0.5}
        invalido={inv('lucroDesejado')} desabilitado={desabilitado} onChange={set('lucroDesejado')}
      />
      <PercentualSimples
        id="p-fixa" rotulo="Despesa fixa" valor={c.despesaFixa} baseFracao={base?.despesaFixa ?? null}
        invalido={inv('despesaFixa')} desabilitado={desabilitado} onChange={set('despesaFixa')}
      />
      <PercentualSimples
        id="p-variavel" rotulo="Despesa variável (total)" valor={c.despesaVariavel} baseFracao={base?.despesaVariavel ?? null}
        invalido={inv('despesaVariavel')} desabilitado={desabilitado} onChange={set('despesaVariavel')}
      />
      <PercentualSimples
        id="p-taxa" rotulo="Taxa financeira (a.m.)" valor={c.taxaFinanceira} baseFracao={base?.taxaFinanceira ?? null}
        invalido={inv('taxaFinanceira')} desabilitado={desabilitado} onChange={set('taxaFinanceira')}
      />
      <Dinheiro
        id="p-custo" rotulo="Custo unitário" valor={c.custo} baseDinheiro={base?.custo ?? null} nota="Substitui o custo atual (desliga a variação)." tipo="custo"
        invalido={inv('custo')} desabilitado={desabilitado} onChange={set('custo')}
      />
      <Dinheiro
        id="p-praticado" rotulo="Preço praticado deste cenário" valor={c.praticado} baseDinheiro={null} nota="Com IPI, a prazo. Vazio usa o praticado geral." tipo="dinheiro"
        invalido={inv('praticado')} desabilitado={desabilitado} onChange={set('praticado')}
      />
    </section>
  );
}
